// Les 300 modifications (annexe A2–A4) traduites en modificateurs conditionnels et effets structurels.
// Chaque entrée porte un bénéfice ET sa contrepartie. Les multiplicateurs sont plafonnés côté serveur (MOD_CAPS).
// Règles d'assemblage (dossier §11) :
//  - emplacements aux niveaux 15 (arme), 20 (châssis), 35 (passif), 45 (2e arme), 50 (2e châssis) ;
//  - une seule modification par groupe (« une propriété identique ne s'empile pas ») ;
//  - compatibilité par type de projectile/tube : balistique, obus, missiles, optique, drones, pièges, mines, collision…
import type { ClassNode } from './classes';
import { RAW_MODS } from './data.generated';

export type ModSlot = 'weapon' | 'chassis' | 'passive';

export const SLOT_UNLOCKS: { slot: ModSlot; level: number }[] = [
  { slot: 'weapon', level: 15 },
  { slot: 'chassis', level: 20 },
  { slot: 'passive', level: 35 },
  { slot: 'weapon', level: 45 },
  { slot: 'chassis', level: 50 },
  // Niveau 250, arène libre seulement (choix de Léni, 5 novembre 2026) : 5 emplacements de plus, 10 au total.
  { slot: 'weapon', level: 80 },
  { slot: 'chassis', level: 110 },
  { slot: 'passive', level: 140 },
  { slot: 'weapon', level: 170 },
  { slot: 'chassis', level: 200 },
];

export type Need =
  | 'bullet'
  | 'shell'
  | 'missile'
  | 'beam'
  | 'orb'
  | 'drone'
  | 'trap'
  | 'mine'
  | 'explosive'
  | 'heat'
  | 'multi'
  | 'collision'
  | 'auto'
  | 'unit'
  | 'charge'
  | 'gun';

/** Conditions évaluées au moment de l'effet (état du tank, cible, coup reçu). */
export type Cond =
  // état du tank
  | 'moving'
  | 'stationary'
  | 'still'
  | 'outCombat'
  | 'firing'
  | 'notFiring'
  | 'rested'
  | 'rested3'
  | 'lowHp'
  | 'critHp'
  | 'grey'
  | 'retreat'
  | 'forward'
  | 'aligned'
  | 'turning'
  | 'aimSteady'
  | 'aimTurning'
  | 'noAllies'
  | 'fireLong'
  | 'fireShort'
  | 'altRhythm'
  | 'slow'
  | 'braking'
  | 'greyCalm'
  | 'monsterWhileSlow'
  // cible (dégâts infligés)
  | 'vsShape'
  | 'vsRare'
  | 'vsCommon'
  | 'vsMonster'
  | 'vsWeakMonster'
  | 'vsBoss'
  | 'vsTank'
  | 'vsUnit'
  | 'vsMine'
  | 'vsOrb'
  | 'vsProj'
  | 'nearOwner'
  | 'farOwner'
  | 'young'
  | 'old'
  | 'firstHit'
  | 'oblique'
  | 'central'
  | 'rearBarrel'
  | 'frontBarrel'
  | 'grouped'
  | 'dispersed'
  | 'spaced'
  | 'bodyFront'
  | 'bodySide'
  // coup reçu
  | 'kBallistic'
  | 'kEnergy'
  | 'kExplosive'
  | 'kDirect'
  | 'kContact'
  | 'kFrag'
  | 'fromFront'
  | 'fromSide'
  | 'fromRear'
  | 'fromMonster1'
  | 'fromMonster2'
  | 'fromMonster3'
  | 'fromBoss'
  | 'monsterContact'
  | 'monsterRanged'
  | 'small'
  | 'mainAggressor'
  | 'sustained';

/** Clés de statistiques modifiables (multiplicatives ; suffixe « + » = additif). */
export type StatKey =
  | 'dmg'
  | 'pen'
  | 'speed'
  | 'life'
  | 'reload'
  | 'spread'
  | 'size'
  | 'recoil'
  | 'pellets'
  | 'inherit'
  | 'drag'
  | 'homing'
  | 'accel'
  | 'control'
  | 'droneSpeed'
  | 'droneResp'
  | 'maxActive'
  | 'armDelay'
  | 'trigger'
  | 'explodeR'
  | 'explodeDmg'
  | 'heat'
  | 'cool'
  | 'ramp'
  | 'knock+'
  | 'bounces+'
  | 'pierce+'
  | 'chargeTime'
  | 'autoRate'
  | 'autoTurn'
  | 'dealt'
  | 'hp'
  | 'move'
  | 'resp'
  | 'hullTurn'
  | 'regen'
  | 'regenDelay'
  | 'taken'
  | 'knockTaken'
  | 'pullTaken'
  | 'slowTaken'
  | 'ctrlDur'
  | 'body'
  | 'bodyInterval'
  | 'bodyKnock+'
  | 'unitLife'
  | 'xp'
  | 'spread+'
  | 'drag+'
  | 'accel+'
  | 'homing+'
  | 'autoLife'
  | 'effDur';

export interface Mod {
  k: StatKey;
  v: number;
  c?: Cond;
}

export type FragDir = 'fwd' | 'side' | 'back' | 'aim' | 'all';

export type Fx =
  | { t: 'pierce'; n: number }
  | { t: 'noPierce' }
  | { t: 'frags'; count: number; spread: number; dmg: number; speed: number; life: number; on: 'hit' | 'expire' | 'age' | 'intercept'; age?: number; dir: FragDir; keepCore?: boolean }
  | { t: 'noFrags' }
  | { t: 'curve'; rate: number }
  | { t: 'wave'; amp: number; freq: number }
  | { t: 'spiral'; rate: number }
  | { t: 'return'; after: number; dmgBack: number }
  | { t: 'deflect'; range: number; mul: number }
  | { t: 'delayLaunch'; delay: number }
  | { t: 'falloff'; k: number }
  | { t: 'burst'; count: number; interval: number }
  | { t: 'unburst' }
  | { t: 'sync' }
  | { t: 'alt' }
  | { t: 'nth'; n: number; mul: number }
  | { t: 'first'; rest: number; mul: number; speed?: number }
  | { t: 'afterOverheat'; mul: number }
  | { t: 'rampUp'; max: number; perShot: number; heat: number }
  | { t: 'gradRegen'; per: number; max: number }
  | { t: 'noRegenContact'; window: number }
  | { t: 'focusSpread'; time: number; min: number }
  | { t: 'armorPierce'; k: number }
  | { t: 'echo'; delay: number; mul: number }
  | { t: 'dock' }
  | { t: 'stack'; per: number; max: number; window: number; monstersOnly?: boolean; tanksOnly?: boolean }
  | { t: 'pattern'; muls: number[] }
  | { t: 'safety'; stopAt: number; resumeAt: number }
  | { t: 'noOverheatSlow' }
  | { t: 'fuse'; delay: number }
  | { t: 'ring' }
  | { t: 'cone'; deg: number; mul: number; off: number }
  | { t: 'stick'; delay: number }
  | { t: 'clearProj'; maxPen: number }
  | { t: 'double' }
  | { t: 'homingWindow'; from: number; to: number }
  | { t: 'lockOn'; radius: number }
  | { t: 'brake'; dist: number }
  | { t: 'airburst'; at: number }
  | { t: 'dying'; time: number }
  | { t: 'recycle'; refund: number }
  | { t: 'droneReturnLow'; at: number }
  | { t: 'droneAlt' }
  | { t: 'droneRing'; radius: number }
  | { t: 'droneArrow' }
  | { t: 'droneGuard'; radius: number }
  | { t: 'unitRegenIdle'; rate: number }
  | { t: 'escort'; radius: number; mul: number }
  | { t: 'shield'; pct: number; delay: number; rate: number; arc?: 'front' | 'rear'; filter?: 'contact' | 'small' | 'big'; share?: number; visible?: boolean }
  | { t: 'droneShield'; share: number }
  | { t: 'reactive'; rest: number; mul: number; energyOnly?: boolean }
  | { t: 'ablative'; window: number; absorb: number; cap: number; cooldown: number }
  | { t: 'lastPlate'; threshold: number; mul: number }
  | { t: 'flee'; below: number; mul: number; dur: number; cd: number }
  | { t: 'repli'; mul: number; dur: number; cd: number }
  | { t: 'borderShield'; pct: number; dur: number; cd: number }
  | { t: 'explorer'; mul: number; cancel: number }
  | { t: 'cruise'; mul: number; after: number }
  | { t: 'skirmish'; mul: number; dur: number }
  | { t: 'surge'; first: number; mulFirst: number; mulAfter: number; pause?: number }
  | { t: 'momentum'; speed: number; damage: number; build: number }
  | { t: 'contactSlow'; mul: number; dur: number; self: number }
  | { t: 'info'; what: InfoKind }
  | { t: 'ally'; radius: number; taken: number }
  | { t: 'successive'; window: number; mul: number }
  | { t: 'decor'; v: 'compact' | 'heavy' }
  | { t: 'rangeToCursor' }
  | { t: 'groupBonus'; per: number; max: number; single: number }
  | { t: 'turnLimit'; rate: number }
  // Modules visibles du niveau 250 (shared/catalog/modules.ts) : tube automatique ajouté à l'arme effective.
  | { t: 'modTurret' }
  | { t: 'modDrones'; count: number };

export type InfoKind =
  | 'radar'
  | 'bossCompass'
  | 'trails'
  | 'sonar'
  | 'rearAlert'
  | 'mineSight'
  | 'aggressor'
  | 'aggro'
  | 'telemetry'
  | 'allyBeacon'
  | 'rangeRing'
  | 'zoneIntel'
  | 'tracer';

export interface ModDef {
  id: string;
  slot: ModSlot;
  group: string;
  groupId: string;
  name: string;
  desc: string;
  /** Au moins un de ces traits doit être présent sur la classe (aucun = universel). */
  needs?: Need[];
  /** Incompatible avec ces traits (ex. ricochet et guidage). */
  forbid?: Need[];
  mods: Mod[];
  fx: Fx[];
  /** L'effet ne joue qu'en présence d'alliés (modes équipes) : sans effet en FFA. */
  teamOnly?: boolean;
}

const M = (k: StatKey, v: number, c?: Cond): Mod => (c ? { k, v, c } : { k, v });

type Entry = [needs: Need[] | null, mods: Mod[], fx?: Fx[], extra?: Partial<Pick<ModDef, 'forbid' | 'teamOnly'>>];

const BAL: Need[] = ['bullet'];
const PROJ: Need[] = ['bullet', 'shell', 'missile', 'beam', 'orb'];
const GUN: Need[] = ['gun'];
const EXPL: Need[] = ['explosive'];
const HEAT: Need[] = ['heat'];
const UNITS: Need[] = ['drone', 'trap', 'mine'];

const TABLE: Record<string, Entry> = {
  // ── W01 Balistique ────────────────────────────────────────
  'W01-01': [BAL, [M('dmg', 0.85)], [{ t: 'pierce', n: 1 }]],
  'W01-02': [BAL, [M('pen', 1.4), M('speed', 0.85)]],
  'W01-03': [BAL, [M('speed', 1.25), M('dmg', 0.88)]],
  'W01-04': [BAL, [M('dmg', 1.25)], [{ t: 'noPierce' }]],
  'W01-05': [BAL, [M('size', 0.7), M('speed', 1.2), M('dmg', 0.85)]],
  'W01-06': [BAL, [M('knock+', 120), M('speed', 0.75)]],
  'W01-07': [BAL, [M('drag+', 0.9), M('life', 1.6)]],
  'W01-08': [BAL, [M('speed', 0.6), M('accel+', 1.4), M('dealt', 0.8, 'young')]],
  'W01-09': [BAL, [M('life', 1.5)], [{ t: 'falloff', k: 0.55 }]],
  'W01-10': [BAL, [M('dealt', 1.3, 'young'), M('life', 0.7)]],

  // ── W02 Fragments ─────────────────────────────────────────
  'W02-01': [['bullet', 'shell'], [M('dmg', 0.85)], [{ t: 'frags', count: 2, spread: 20, dmg: 0.35, speed: 0.8, life: 0.3, on: 'hit', dir: 'fwd' }]],
  'W02-02': [['bullet', 'shell'], [M('dmg', 0.85)], [{ t: 'frags', count: 2, spread: 180, dmg: 0.35, speed: 0.8, life: 0.3, on: 'hit', dir: 'side' }]],
  'W02-03': [['bullet', 'shell'], [M('dmg', 0.8)], [{ t: 'frags', count: 1, spread: 0, dmg: 0.4, speed: 0.8, life: 0.35, on: 'hit', dir: 'back' }]],
  'W02-04': [['bullet', 'shell'], [M('dmg', 0.9)], [{ t: 'frags', count: 3, spread: 40, dmg: 0.4, speed: 0.9, life: 0.4, on: 'age', age: 0.45, dir: 'fwd' }]],
  'W02-05': [['bullet', 'shell'], [M('dmg', 0.95)], [{ t: 'frags', count: 5, spread: 360, dmg: 0.3, speed: 0.7, life: 0.3, on: 'expire', dir: 'all' }]],
  'W02-06': [['bullet', 'shell'], [M('pen', 0.7)], [{ t: 'frags', count: 3, spread: 60, dmg: 0.4, speed: 0.8, life: 0.3, on: 'intercept', dir: 'fwd' }]],
  'W02-07': [['bullet', 'shell'], [M('dmg', 0.9)], [{ t: 'frags', count: 2, spread: 30, dmg: 0.55, speed: 0.55, life: 0.45, on: 'hit', dir: 'fwd' }]],
  'W02-08': [['bullet', 'shell'], [M('dmg', 0.9)], [{ t: 'frags', count: 8, spread: 360, dmg: 0.12, speed: 0.9, life: 0.15, on: 'hit', dir: 'all' }]],
  'W02-09': [['bullet', 'shell'], [M('dmg', 0.9)], [{ t: 'frags', count: 3, spread: 30, dmg: 0.35, speed: 0.85, life: 0.35, on: 'hit', dir: 'aim' }]],
  'W02-10': [['bullet', 'shell'], [M('dmg', 0.9)], [{ t: 'frags', count: 2, spread: 50, dmg: 0.25, speed: 0.8, life: 0.3, on: 'hit', dir: 'fwd', keepCore: true }]],

  // ── W03 Cadences ──────────────────────────────────────────
  'W03-01': [GUN, [M('reload', 2)], [{ t: 'burst', count: 2, interval: 0.08 }]],
  'W03-02': [GUN, [M('reload', 3.3)], [{ t: 'burst', count: 3, interval: 0.08 }]],
  'W03-03': [['multi'], [M('reload', 1.15)], [{ t: 'alt' }]],
  'W03-04': [['multi'], [M('reload', 1.35)], [{ t: 'sync' }]],
  'W03-05': [GUN, [], [{ t: 'rampUp', max: 1.6, perShot: 0.05, heat: 0.03 }]],
  'W03-06': [GUN, [M('reload', 1.15)], [{ t: 'first', rest: 1.5, mul: 1.8 }]],
  'W03-07': [GUN, [M('reload', 1.15)], [{ t: 'nth', n: 4, mul: 1.8 }]],
  'W03-08': [GUN, [M('reload', 0.85, 'moving'), M('spread', 1.4, 'moving')]],
  'W03-09': [GUN, [M('reload', 0.85, 'stationary'), M('move', 0.92)]],
  'W03-10': [GUN, [], [{ t: 'pattern', muls: [0.7, 1.3] }]],

  // ── W04 Trajectoires ──────────────────────────────────────
  'W04-01': [BAL, [M('spread', 0.9)], [{ t: 'curve', rate: 0.9 }]],
  'W04-02': [BAL, [M('spread', 0.9)], [{ t: 'curve', rate: -0.9 }]],
  'W04-03': [BAL, [M('spread', 1.3)], [{ t: 'wave', amp: 2.2, freq: 7 }]],
  'W04-04': [BAL, [M('life', 0.8)], [{ t: 'spiral', rate: 3 }]],
  'W04-05': [BAL, [], [{ t: 'return', after: 0.6, dmgBack: 0.7 }]],
  'W04-06': [BAL, [M('bounces+', 1)], [], { forbid: ['missile'] }],
  'W04-07': [BAL, [], [{ t: 'deflect', range: 300, mul: 0.65 }]],
  'W04-08': [BAL, [M('inherit', 3), M('spread', 1.2)]],
  'W04-09': [BAL, [M('inherit', 0), M('recoil', 1.4)]],
  'W04-10': [BAL, [M('dmg', 1.1)], [{ t: 'delayLaunch', delay: 0.25 }]],

  // ── W05 Explosifs ─────────────────────────────────────────
  'W05-01': [EXPL, [M('explodeDmg', 1.3), M('explodeR', 0.8)]],
  'W05-02': [EXPL, [M('explodeR', 1.35), M('explodeDmg', 0.8)]],
  'W05-03': [EXPL, [M('explodeR', 1.1)], [{ t: 'ring' }]],
  'W05-04': [EXPL, [], [{ t: 'cone', deg: 70, mul: 1.4, off: 0.3 }]],
  'W05-05': [EXPL, [M('explodeDmg', 1.2)], [{ t: 'fuse', delay: 0.5 }]],
  'W05-06': [EXPL, [M('homing', 0)], [{ t: 'airburst', at: 0.6 }]],
  'W05-07': [EXPL, [M('explodeDmg', 0.85)], [{ t: 'stick', delay: 0.5 }]],
  'W05-08': [EXPL, [M('knock+', 260), M('explodeDmg', 0.7)]],
  'W05-09': [EXPL, [M('explodeDmg', 0.7)], [{ t: 'clearProj', maxPen: 30 }]],
  'W05-10': [EXPL, [M('explodeDmg', 0.5), M('explodeR', 0.8)], [{ t: 'double' }]],

  // ── W06 Missiles ──────────────────────────────────────────
  'W06-01': [['missile'], [M('homing+', 1.5), M('speed', 0.85)]],
  'W06-02': [['missile'], [M('speed', 1.3), M('homing', 0.6)]],
  'W06-03': [['missile'], [M('homing+', 1.6)], [{ t: 'homingWindow', from: 0, to: 0.4 }]],
  'W06-04': [['missile'], [M('homing+', 2)], [{ t: 'homingWindow', from: 0.5, to: 99 }]],
  'W06-05': [['missile'], [M('homing+', 1.5)], [{ t: 'lockOn', radius: 220 }]],
  'W06-06': [['missile'], [M('spread', 2), M('reload', 1.15)]],
  'W06-07': [['missile'], [M('pellets', 2), M('dmg', 0.6)]],
  'W06-08': [['missile'], [M('pen', 1.8), M('speed', 0.8)]],
  'W06-09': [['missile'], [M('speed', 1.3), M('life', 0.7)]],
  'W06-10': [['missile'], [M('pen', 0.8)], [{ t: 'brake', dist: 160 }]],

  // ── W07 Optique ───────────────────────────────────────────
  'W07-01': [['beam'], [M('size', 0.7), M('dmg', 1.25)]],
  'W07-02': [['beam'], [M('size', 1.5), M('dmg', 0.75)]],
  'W07-03': [['beam'], [M('dmg', 1.15), M('reload', 1.3)]],
  'W07-04': [['beam'], [], [{ t: 'stack', per: 0.1, max: 1, window: 0.4 }]],
  'W07-05': [['beam'], [M('size', 1.2)], [{ t: 'turnLimit', rate: 3 }]],
  'W07-06': [['beam'], [M('pellets', 2), M('dmg', 0.55), M('spread+', 6)]],
  'W07-07': [['beam'], [M('heat', 0.6), M('dmg', 0.85)]],
  'W07-08': [['beam'], [M('dmg', 1.2), M('heat', 1.4)]],
  'W07-09': [['beam'], [M('bounces+', 1), M('life', 0.8)]],
  'W07-10': [['beam'], [M('dmg', 0.85)], [{ t: 'echo', delay: 0.15, mul: 0.4 }]],

  // ── W08 Drones ────────────────────────────────────────────
  'W08-01': [['drone'], [M('pen', 1.5), M('droneSpeed', 0.8)]],
  'W08-02': [['drone'], [M('droneSpeed', 1.35), M('pen', 0.7)]],
  'W08-03': [['drone'], [M('dealt', 1.12)], [{ t: 'droneArrow' }]],
  'W08-04': [['drone'], [M('control', 0.8)], [{ t: 'droneRing', radius: 2.2 }]],
  'W08-05': [['drone'], [], [{ t: 'droneReturnLow', at: 0.35 }]],
  'W08-06': [['drone'], [M('reload', 0.7), M('maxActive', 0.75)]],
  'W08-07': [['drone'], [M('control', 1.4), M('dmg', 0.85)]],
  'W08-08': [['drone'], [M('control', 0.6), M('pen', 1.2)], [{ t: 'droneGuard', radius: 260 }]],
  'W08-09': [['drone'], [M('dmg', 0.9)], [{ t: 'droneAlt' }]],
  'W08-10': [['drone'], [], [{ t: 'dying', time: 0.6 }]],

  // ── W09 Mines et pièges ───────────────────────────────────
  'W09-01': [['mine', 'trap'], [M('armDelay', 0.5), M('explodeDmg', 0.8), M('dmg', 0.9)]],
  'W09-02': [['mine', 'trap'], [M('armDelay', 1.8), M('explodeDmg', 1.35), M('dmg', 1.15), M('pen', 0.7)]],
  'W09-03': [['mine', 'trap'], [M('life', 1.6), M('maxActive', 0.7)]],
  'W09-04': [['mine', 'trap'], [M('maxActive', 1.5), M('dmg', 0.7), M('explodeDmg', 0.7)]],
  'W09-05': [['mine', 'trap'], [M('spread', 0), M('speed', 1.25)]],
  'W09-06': [['mine', 'trap'], [M('spread+', 25), M('speed', 1.1)]],
  'W09-07': [['mine', 'trap'], [M('drag', 0.4)]],
  'W09-08': [['mine', 'trap'], [M('trigger', 1.6), M('size', 1.35)]],
  'W09-09': [['mine', 'trap'], [M('explodeDmg', 1.4), M('dmg', 1.1)], [{ t: 'noFrags' }]],
  'W09-10': [['mine', 'trap'], [M('reload', 0.9)], [{ t: 'recycle', refund: 0.5 }]],

  // ── W10 Collision offensive ───────────────────────────────
  'W10-01': [['collision'], [M('body', 1.25), M('move', 0.9)]],
  'W10-02': [['collision'], [M('body', 1.4, 'bodyFront'), M('body', 0.7, 'bodySide')]],
  'W10-03': [['collision'], [M('body', 1.35, 'bodySide'), M('body', 0.8, 'bodyFront')]],
  'W10-04': [['collision'], [M('hullTurn', 0.7)], [{ t: 'momentum', speed: 0.1, damage: 0.8, build: 0.7 }]],
  'W10-05': [['collision'], [M('bodyInterval', 0.6), M('body', 0.65)]],
  'W10-06': [['collision'], [M('body', 1.8), M('bodyInterval', 2.2)]],
  'W10-07': [['collision'], [M('bodyKnock+', 200), M('body', 0.8)]],
  'W10-08': [['collision'], [], [{ t: 'contactSlow', mul: 0.7, dur: 0.6, self: 0.85 }]],
  'W10-09': [['collision'], [M('knockTaken', 0.6), M('resp', 0.85)]],
  'W10-10': [['collision'], [M('body', 1.5), M('hp', 0.85)]],

  // ── W11 Interception ──────────────────────────────────────
  'W11-01': [BAL, [M('dealt', 1.8, 'vsProj'), M('reload', 1.15)]],
  'W11-02': [BAL, [M('dealt', 2, 'vsProj'), M('dealt', 0.85, 'vsTank')]],
  'W11-03': [BAL, [M('pellets', 3), M('spread+', 18), M('life', 0.4), M('dmg', 0.6)]],
  'W11-04': [BAL, [M('size', 1.5), M('pen', 0.9)]],
  'W11-05': [BAL, [M('taken', 0.5, 'kFrag'), M('dmg', 0.85)]],
  'W11-06': [BAL, [M('life', 1.4), M('speed', 0.75)]],
  'W11-07': [BAL, [M('size', 2.2), M('speed', 0.5), M('reload', 1.4), M('pen', 1.5)]],
  'W11-08': [BAL, [M('dealt', 1.8, 'vsUnit'), M('dealt', 0.85, 'vsTank')]],
  'W11-09': [BAL, [M('dealt', 2, 'vsMine'), M('dealt', 0.8, 'vsShape')]],
  'W11-10': [BAL, [M('dealt', 2.5, 'vsOrb'), M('reload', 1.05)]],

  // ── W12 Ciblage et portée ─────────────────────────────────
  'W12-01': [PROJ, [M('move', 0.85, 'firing')], [{ t: 'focusSpread', time: 1.5, min: 0.4 }]],
  'W12-02': [PROJ, [M('spread', 0.6, 'moving'), M('dmg', 0.9)]],
  'W12-03': [PROJ, [M('life', 1.3, 'still'), M('resp', 0.85)]],
  'W12-04': [EXPL, [], [{ t: 'groupBonus', per: 0.1, max: 0.3, single: 0.9 }]],
  'W12-05': [PROJ, [], [{ t: 'stack', per: 0.05, max: 0.3, window: 1.2, tanksOnly: true }]],
  'W12-06': [PROJ, [M('dealt', 1.25, 'vsMonster'), M('dealt', 0.85, 'vsTank')]],
  'W12-07': [PROJ, [M('dealt', 1.3, 'vsShape'), M('dealt', 0.85, 'vsTank')]],
  'W12-08': [PROJ, [M('dmg', 0.9)], [{ t: 'armorPierce', k: 0.5 }]],
  'W12-09': [PROJ, [M('life', 0.7), M('speed', 1.25)]],
  'W12-10': [['bullet', 'shell', 'missile', 'orb'], [M('reload', 1.2)], [{ t: 'rangeToCursor' }]],

  // ── H01 Mobilité ──────────────────────────────────────────
  'H01-01': [null, [M('resp', 1.4), M('hp', 0.9)]],
  'H01-02': [null, [M('hp', 1.15), M('resp', 0.7)]],
  'H01-03': [null, [M('move', 1.1, 'aligned'), M('resp', 0.75, 'braking')]],
  'H01-04': [null, [M('hullTurn', 2), M('resp', 1.2), M('recoil', 1.3)]],
  'H01-05': [null, [M('resp', 1.8, 'braking'), M('move', 0.92)]],
  'H01-06': [null, [M('resp', 0.6), M('move', 1.06)]],
  'H01-07': [null, [M('move', 1.15, 'retreat'), M('move', 0.93, 'forward')]],
  'H01-08': [null, [M('knockTaken', 0.6), M('pullTaken', 0.7), M('resp', 0.85)]],
  'H01-09': [null, [M('resp', 0.75), M('move', 1.08)]],
  'H01-10': [null, [], [{ t: 'cruise', mul: 1.15, after: 3 }]],

  // ── H02 Blindage ──────────────────────────────────────────
  'H02-01': [null, [M('taken', 0.75, 'fromFront'), M('taken', 1.2, 'fromRear')]],
  'H02-02': [null, [M('taken', 0.75, 'fromSide'), M('taken', 1.15, 'fromFront')]],
  'H02-03': [null, [M('taken', 0.7, 'fromRear'), M('taken', 1.15, 'fromFront')]],
  'H02-04': [null, [M('taken', 0.92), M('move', 0.94)]],
  'H02-05': [null, [M('taken', 0.8, 'kBallistic'), M('taken', 1.2, 'kEnergy')]],
  'H02-06': [null, [M('taken', 0.8, 'kEnergy'), M('taken', 1.15, 'kBallistic')]],
  'H02-07': [null, [M('taken', 0.7, 'kExplosive'), M('taken', 1.1, 'kDirect')]],
  'H02-08': [null, [M('taken', 0.7, 'kContact'), M('dmg', 0.9)]],
  'H02-09': [null, [M('hp', 1.2), M('move', 0.88)]],
  'H02-10': [null, [M('move', 1.06), M('taken', 1.15, 'sustained')]],

  // ── H03 Régénération ──────────────────────────────────────
  'H03-01': [null, [M('regen', 1.5), M('regenDelay', 1.25)]],
  'H03-02': [null, [M('regenDelay', 0.6), M('regen', 0.7)]],
  'H03-03': [null, [M('hp', 1.12), M('regen', 0.75)]],
  'H03-04': [null, [M('regen', 1.3, 'moving'), M('regen', 0.7, 'stationary')]],
  'H03-05': [null, [M('regen', 1.6, 'stationary'), M('taken', 1.08, 'stationary')]],
  'H03-06': [null, [M('regen', 0.9)], [{ t: 'gradRegen', per: 0.1, max: 2 }]],
  'H03-07': [null, [M('hp', 0.92)], [{ t: 'shield', pct: 0.08, delay: 12, rate: 0.15 }]],
  'H03-08': [['drone'], [M('regen', 0.8)], [{ t: 'unitRegenIdle', rate: 0.3 }]],
  'H03-09': [null, [M('regen', 1.2), M('regenDelay', 0.85), M('hp', 0.9)]],
  'H03-10': [null, [M('regen', 2.2, 'critHp')], [{ t: 'noRegenContact', window: 2 }]],

  // ── H04 Recul ─────────────────────────────────────────────
  'H04-01': [GUN, [M('recoil', 0.4), M('move', 0.94)]],
  'H04-02': [GUN, [M('recoil', 2), M('spread', 1.15)]],
  'H04-03': [GUN, [M('spread', 0.75), M('resp', 0.85)]],
  'H04-04': [null, [M('knockTaken', 0.5, 'stationary'), M('recoil', 0.5, 'stationary'), M('resp', 0.85)]],
  'H04-05': [null, [M('knockTaken', 0.7), M('pullTaken', 0.8), M('move', 0.95)]],
  'H04-06': [GUN, [M('recoil', 0.6), M('reload', 1.08)]],
  'H04-07': [null, [M('knockTaken', 1.3), M('resp', 0.9)]],
  'H04-08': [GUN, [M('recoil', 1.6, 'retreat'), M('move', 0.94, 'forward')]],
  'H04-09': [GUN, [M('spread', 0.85)], [{ t: 'turnLimit', rate: 6 }]],
  'H04-10': [GUN, [M('recoil', 0.5), M('resp', 0.85)]],

  // ── H05 Thermique ─────────────────────────────────────────
  'H05-01': [HEAT, [M('cool', 1.5), M('hp', 0.92)]],
  'H05-02': [HEAT, [M('heat', 0.75), M('cool', 0.75)]],
  'H05-03': [HEAT, [M('heat', 0.5), M('dmg', 0.85)]],
  'H05-04': [HEAT, [M('dmg', 1.2), M('heat', 1.5)]],
  'H05-05': [HEAT, [M('cool', 1.5, 'moving'), M('cool', 0.7, 'stationary')]],
  'H05-06': [HEAT, [M('cool', 1.6, 'stationary'), M('move', 0.94)]],
  'H05-07': [HEAT, [M('reload', 1.1)], [{ t: 'safety', stopAt: 0.9, resumeAt: 0.7 }]],
  'H05-08': [HEAT, [M('cool', 2, 'notFiring'), M('reload', 1.12, 'fireLong')]],
  'H05-09': [HEAT, [M('heat', 0.7), M('cool', 0.7)]],
  'H05-10': [HEAT, [M('dmg', 0.9)], [{ t: 'noOverheatSlow' }]],

  // ── H06 Boucliers passifs ─────────────────────────────────
  'H06-01': [null, [M('hp', 0.9)], [{ t: 'shield', pct: 0.12, delay: 5, rate: 0.25 }]],
  'H06-02': [null, [], [{ t: 'shield', pct: 0.2, delay: 6, rate: 0.2, arc: 'front' }]],
  'H06-03': [null, [M('dmg', 0.92)], [{ t: 'shield', pct: 0.2, delay: 6, rate: 0.2, arc: 'rear' }]],
  'H06-04': [null, [], [{ t: 'shield', pct: 0.3, delay: 10, rate: 1, filter: 'contact' }]],
  'H06-05': [null, [], [{ t: 'shield', pct: 0.15, delay: 4, rate: 0.25, filter: 'small' }]],
  'H06-06': [null, [M('hp', 0.92)], [{ t: 'shield', pct: 0.15, delay: 4, rate: 0.2, share: 0.15 }]],
  'H06-07': [null, [], [{ t: 'shield', pct: 0.25, delay: 15, rate: 1, filter: 'big' }]],
  'H06-08': [null, [M('regen', 0.6)], [{ t: 'shield', pct: 0.15, delay: 5, rate: 0.25 }]],
  'H06-09': [['drone'], [], [{ t: 'droneShield', share: 0.3 }]],
  'H06-10': [null, [], [{ t: 'shield', pct: 0.22, delay: 6, rate: 0.2, visible: true }]],

  // ── H07 Détection ─────────────────────────────────────────
  'H07-01': [null, [], [{ t: 'info', what: 'radar' }]],
  'H07-02': [null, [], [{ t: 'info', what: 'bossCompass' }]],
  'H07-03': [null, [], [{ t: 'info', what: 'trails' }]],
  'H07-04': [null, [], [{ t: 'info', what: 'sonar' }]],
  'H07-05': [null, [], [{ t: 'info', what: 'rearAlert' }]],
  'H07-06': [null, [M('hp', 0.92)], [{ t: 'info', what: 'mineSight' }]],
  'H07-07': [null, [], [{ t: 'info', what: 'aggressor' }]],
  'H07-08': [null, [M('dealt', 0.92, 'vsTank')], [{ t: 'info', what: 'aggro' }]],
  'H07-09': [null, [], [{ t: 'info', what: 'telemetry' }]],
  'H07-10': [null, [], [{ t: 'info', what: 'allyBeacon' }], { teamOnly: true }],

  // ── H08 Résistances contrôle ──────────────────────────────
  'H08-01': [null, [M('slowTaken', 0.5), M('move', 0.95)]],
  'H08-02': [null, [M('pullTaken', 0.5), M('knockTaken', 1.2)]],
  'H08-03': [null, [M('knockTaken', 0.5), M('pullTaken', 1.2)]],
  'H08-04': [null, [M('ctrlDur', 0.6), M('hp', 0.92)]],
  'H08-05': [null, [M('knockTaken', 0.7), M('pullTaken', 0.7), M('resp', 0.85)]],
  'H08-06': [null, [M('ctrlDur', 0.6, 'moving'), M('ctrlDur', 1.2, 'stationary')]],
  'H08-07': [null, [M('slowTaken', 0.6), M('taken', 1.1, 'kBallistic')]],
  'H08-08': [null, [M('knockTaken', 0.5, 'kContact'), M('taken', 0.9, 'kContact')]],
  'H08-09': [null, [], [{ t: 'successive', window: 3, mul: 0.5 }]],
  'H08-10': [null, [M('ctrlDur', 0.5, 'fromFront'), M('ctrlDur', 1.2, 'fromSide')]],

  // ── H09 Gestion unités ────────────────────────────────────
  'H09-01': [['drone'], [M('reload', 0.75)]],
  'H09-02': [['drone'], [M('reload', 0.6), M('dmg', 0.85)]],
  'H09-03': [['drone'], [M('droneResp', 1.6), M('control', 0.65)]],
  'H09-04': [['drone'], [M('control', 1.5), M('droneResp', 0.6)]],
  'H09-05': [['mine'], [M('maxActive', 1.5), M('move', 0.92)]],
  'H09-06': [['trap'], [M('life', 1.5), M('maxActive', 0.75)]],
  'H09-07': [['auto'], [M('autoTurn', 1.8), M('autoLife', 0.75)]],
  'H09-08': [UNITS, [M('hp', 0.92)], [{ t: 'unitRegenIdle', rate: 0.25 }]],
  'H09-09': [['drone'], [], [{ t: 'escort', radius: 220, mul: 0.85 }]],
  'H09-10': [['drone'], [M('reload', 1.2)], [{ t: 'dock' }]],

  // ── H10 Économie de tir ───────────────────────────────────
  'H10-01': [GUN, [M('reload', 1.3)], [{ t: 'burst', count: 2, interval: 0.09 }]],
  'H10-02': [GUN, [M('reload', 0.82), M('dmg', 0.9)]],
  'H10-03': [GUN, [M('reload', 0.95), M('dmg', 0.95)], [{ t: 'unburst' }]],
  'H10-04': [HEAT, [M('heat', 0.6), M('dmg', 0.88)]],
  'H10-05': [GUN, [M('dmg', 1.25), M('resp', 0.8)]],
  'H10-06': [['multi'], [M('heat', 0.7), M('spread', 1.1)], [{ t: 'alt' }]],
  'H10-07': [GUN, [M('reload', 1.1)], [{ t: 'first', rest: 1.5, mul: 1.15, speed: 1.4 }]],
  'H10-08': [GUN, [M('ramp', 0.7), M('cool', 1.3)]],
  'H10-09': [GUN, [], [{ t: 'surge', first: 3, mulFirst: 0.7, mulAfter: 1.15 }]],
  'H10-10': [GUN, [M('dmg', 0.85)], [{ t: 'safety', stopAt: 0.97, resumeAt: 0.85 }, { t: 'unburst' }]],

  // ── H11 Terrain et zones ──────────────────────────────────
  'H11-01': [null, [M('move', 1.12, 'greyCalm')]],
  'H11-02': [null, [M('taken', 0.75, 'fromMonster1'), M('hp', 0.95)]],
  'H11-03': [null, [M('taken', 0.75, 'fromMonster2')]],
  'H11-04': [null, [M('taken', 0.8, 'fromMonster3'), M('move', 0.92)]],
  'H11-05': [null, [M('taken', 0.75, 'fromBoss'), M('knockTaken', 0.6)]],
  'H11-06': [null, [], [{ t: 'explorer', mul: 1.12, cancel: 5 }]],
  'H11-07': [null, [M('hp', 0.94)], [{ t: 'borderShield', pct: 0.1, dur: 3, cd: 30 }]],
  'H11-08': [null, [], [{ t: 'info', what: 'zoneIntel' }]],
  'H11-09': [null, [M('move', 1.1, 'aligned'), M('resp', 0.8, 'turning')]],
  'H11-10': [null, [M('taken', 0.7, 'monsterWhileSlow'), M('move', 0.95)]],

  // ── H12 Profils défensifs ─────────────────────────────────
  'H12-01': [null, [M('move', 1.08), M('hp', 0.9)], [{ t: 'decor', v: 'compact' }]],
  'H12-02': [null, [M('hp', 1.2), M('move', 0.9)], [{ t: 'decor', v: 'heavy' }]],
  'H12-03': [null, [], [{ t: 'reactive', rest: 4, mul: 0.6 }]],
  'H12-04': [null, [], [{ t: 'ablative', window: 1, absorb: 0.3, cap: 0.15, cooldown: 12 }]],
  'H12-05': [null, [M('taken', 0.8, 'small')]],
  'H12-06': [null, [M('taken', 0.85, 'mainAggressor')]],
  'H12-07': [null, [M('dmg', 0.9)], [{ t: 'flee', below: 0.3, mul: 1.2, dur: 4, cd: 20 }]],
  'H12-08': [null, [M('move', 0.94)], [{ t: 'ally', radius: 400, taken: 0.9 }], { teamOnly: true }],
  'H12-09': [null, [M('regen', 1.3, 'noAllies'), M('hp', 0.92)]],
  'H12-10': [null, [], [{ t: 'lastPlate', threshold: 0.15, mul: 0.5 }]],

  // ── P01 Précision ─────────────────────────────────────────
  'P01-01': [GUN, [M('spread', 0.5, 'still'), M('spread', 1.1, 'moving')]],
  'P01-02': [GUN, [], [{ t: 'stack', per: 0.03, max: 0.15, window: 1.2 }]],
  'P01-03': [GUN, [M('reload', 1.1)], [{ t: 'first', rest: 1.2, mul: 1.3 }]],
  'P01-04': [GUN, [M('spread', 0.6), M('pellets', 0.75)]],
  'P01-05': [GUN, [M('spread', 0.6, 'oblique'), M('dealt', 1.1, 'oblique'), M('dealt', 0.9, 'central')]],
  'P01-06': [null, [M('dmg', 0.95)], [{ t: 'info', what: 'rangeRing' }]],
  'P01-07': [['charge'], [M('move', 0.9), M('spread', 0.5), M('chargeTime', 0.9)]],
  'P01-08': [GUN, [M('dealt', 1.2, 'firstHit'), M('pen', 0.8)]],
  'P01-09': [GUN, [M('dmg', 1.2), M('pellets', 0.5), M('reload', 1.05)], [{ t: 'noFrags' }]],
  'P01-10': [HEAT, [M('heat', 0.8, 'aimSteady'), M('heat', 1.2, 'aimTurning')]],

  // ── P02 Défense ───────────────────────────────────────────
  'P02-01': [null, [M('dmg', 0.92)], [{ t: 'shield', pct: 0.08, delay: 8, rate: 0.2 }]],
  'P02-02': [null, [M('dmg', 0.9)], [{ t: 'repli', mul: 1.2, dur: 2, cd: 8 }]],
  'P02-03': [null, [M('taken', 0.75, 'kContact'), M('life', 0.9)]],
  'P02-04': [null, [M('taken', 1.1, 'kBallistic')], [{ t: 'reactive', rest: 4, mul: 0.4, energyOnly: true }]],
  'P02-05': [null, [M('taken', 0.75, 'kExplosive'), M('taken', 1.08, 'kDirect')]],
  'P02-06': [null, [M('hp', 0.94)], [{ t: 'successive', window: 3, mul: 0.6 }]],
  'P02-07': [null, [M('regenDelay', 0.75), M('regen', 0.8)]],
  'P02-08': [null, [M('hp', 1.1), M('move', 0.94)]],
  'P02-09': [null, [M('taken', 0.85, 'stationary')]],
  'P02-10': [null, [M('taken', 0.8, 'fromRear'), M('dealt', 0.92, 'frontBarrel')]],

  // ── P03 Chasse PvE ────────────────────────────────────────
  'P03-01': [null, [M('dealt', 1.3, 'vsShape'), M('dealt', 0.88, 'vsTank')]],
  'P03-02': [null, [M('dealt', 1.3, 'vsMonster'), M('dealt', 0.85, 'vsShape')]],
  'P03-03': [null, [M('dealt', 1.25, 'vsBoss'), M('dealt', 0.88, 'vsTank')]],
  'P03-04': [null, [M('dealt', 1.4, 'vsRare'), M('dealt', 0.85, 'vsCommon')]],
  'P03-05': [null, [M('dealt', 1.3, 'vsWeakMonster'), M('dealt', 0.85, 'vsBoss')]],
  'P03-06': [null, [M('move', 0.95)], [{ t: 'info', what: 'aggro' }]],
  'P03-07': [null, [M('dealt', 1.2, 'vsMonster'), M('dealt', 0.88, 'vsTank')]],
  'P03-08': [null, [M('taken', 0.75, 'monsterContact'), M('taken', 1.1, 'monsterRanged')]],
  'P03-09': [null, [M('taken', 0.75, 'monsterRanged'), M('taken', 1.1, 'monsterContact')]],
  'P03-10': [null, [], [{ t: 'stack', per: 0.05, max: 0.25, window: 1, monstersOnly: true }]],

  // ── P04 Unités déployées ──────────────────────────────────
  'P04-01': [['drone'], [M('dealt', 1.15, 'grouped'), M('dealt', 0.9, 'dispersed')]],
  'P04-02': [['drone'], [M('pen', 1.1)], [{ t: 'droneReturnLow', at: 0.5 }]],
  'P04-03': [['drone'], [M('dealt', 1.2, 'nearOwner'), M('control', 0.75)]],
  'P04-04': [['trap'], [M('pen', 1.3, 'spaced'), M('maxActive', 0.75)]],
  'P04-05': [['mine'], [M('dealt', 1.3, 'farOwner'), M('maxActive', 0.7)]],
  'P04-06': [['trap'], [M('life', 1.4), M('hp', 0.92)]],
  'P04-07': [UNITS, [M('reload', 0.7, 'outCombat'), M('reload', 1.2, 'firing')]],
  'P04-08': [['auto'], [M('autoRate', 1.33), M('dmg', 0.85)]],
  'P04-09': [['drone'], [M('droneResp', 1.4), M('control', 0.7)]],
  'P04-10': [['drone'], [M('pen', 1.25), M('reload', 1.25)]],

  // ── P05 Rythme combat ─────────────────────────────────────
  'P05-01': [GUN, [M('reload', 1.08)], [{ t: 'first', rest: 3, mul: 1.6 }]],
  'P05-02': [GUN, [M('heat', 0.7, 'fireLong'), M('dmg', 0.9, 'fireShort')]],
  'P05-03': [GUN, [M('dmg', 0.9, 'fireLong')], [{ t: 'first', rest: 0.5, mul: 1.25 }]],
  'P05-04': [GUN, [M('spread', 0.7, 'altRhythm'), M('spread', 1.05)]],
  'P05-05': [GUN, [M('reload', 0.8, 'lowHp'), M('spread', 1.4, 'lowHp')]],
  'P05-06': [GUN, [M('heat', 0.4, 'rested')], [{ t: 'unburst' }]],
  'P05-07': [GUN, [], [{ t: 'surge', first: 2, mulFirst: 0.75, mulAfter: 1, pause: 1.5 }]],
  'P05-08': [HEAT, [M('heat', 1.15)], [{ t: 'afterOverheat', mul: 1.8 }]],
  'P05-09': [GUN, [], [{ t: 'stack', per: 0.03, max: 0.15, window: 1.5, tanksOnly: true }]],
  'P05-10': [null, [M('dmg', 0.92)], [{ t: 'skirmish', mul: 1.15, dur: 1.5 }]],

  // ── P06 Spécialisations ───────────────────────────────────
  'P06-01': [null, [M('move', 1.1, 'outCombat'), M('hp', 0.92)]],
  'P06-02': [GUN, [M('dealt', 1.3, 'rearBarrel'), M('dealt', 0.9, 'frontBarrel')]],
  'P06-03': [GUN, [M('reload', 0.85, 'still'), M('resp', 0.8)]],
  'P06-04': [null, [M('dealt', 1.1, 'vsTank'), M('dealt', 0.85, 'vsShape'), M('dealt', 0.85, 'vsMonster')]],
  'P06-05': [null, [M('dmg', 0.9)], [{ t: 'ally', radius: 350, taken: 0.85 }], { teamOnly: true }],
  'P06-06': [null, [M('pen', 1.25), M('body', 0.85)]],
  'P06-07': [GUN, [M('cool', 1.2), M('ramp', 0.8), M('dmg', 0.95)]],
  'P06-08': [PROJ, [M('dealt', 1.25, 'old'), M('dealt', 0.85, 'young')]],
  'P06-09': [PROJ, [M('dealt', 1.25, 'young'), M('life', 0.8)]],
  'P06-10': [null, [M('effDur', 1.3), M('unitLife', 1.3), M('dmg', 0.9)]],
};

export const MOD_DEFS: Record<string, ModDef> = {};
for (const raw of RAW_MODS) {
  const e = TABLE[raw.id];
  if (!e) continue;
  const [needs, mods, fx, extra] = e;
  MOD_DEFS[raw.id] = {
    id: raw.id,
    slot: raw.kind,
    group: raw.group,
    groupId: raw.id.slice(0, 3),
    name: raw.name,
    desc: raw.desc,
    needs: needs ?? undefined,
    forbid: extra?.forbid,
    teamOnly: extra?.teamOnly,
    mods,
    fx: fx ?? [],
  };
}

/** Traits d'une classe pour la compatibilité. */
export function classTraits(cls: ClassNode): Set<Need> {
  const t = new Set<Need>();
  const w = cls.weapon;
  const main = w.barrels.filter((b) => !b.auto);
  for (const b of w.barrels) {
    const k = b.proj?.kind;
    if (b.auto) {
      t.add('auto');
      t.add('unit');
      continue;
    }
    if (b.proj?.lob) {
      t.add('shell');
      t.add('gun');
    } else if (k === 'drone' || k === 'trap' || k === 'mine') {
      t.add(k);
      t.add('unit');
    } else if (k === 'missile' || k === 'beam' || k === 'orb') {
      t.add(k);
      t.add('gun');
    } else {
      t.add('bullet');
      t.add('gun');
    }
    if (b.proj?.explode || b.proj?.lob) t.add('explosive');
  }
  if (w.heat) t.add('heat');
  if (w.charge) t.add('charge');
  if (main.length >= 2) t.add('multi');
  if (cls.family === 'Collision') t.add('collision');
  return t;
}

export function modCompatible(cls: ClassNode, def: ModDef): boolean {
  const traits = classTraits(cls);
  if (def.forbid?.some((f) => traits.has(f))) return false;
  if (!def.needs || def.needs.length === 0) return true;
  return def.needs.some((n) => traits.has(n));
}

/** Emplacements ouverts au niveau donné (compte par type). */
export function slotsAt(level: number): Record<ModSlot, number> {
  const r: Record<ModSlot, number> = { weapon: 0, chassis: 0, passive: 0 };
  for (const s of SLOT_UNLOCKS) if (level >= s.level) r[s.slot]++;
  return r;
}

/** Vérifie qu'une modification peut être ajoutée à l'équipement courant. */
export function canEquip(cls: ClassNode, level: number, equipped: string[], id: string): boolean {
  const def = MOD_DEFS[id];
  if (!def || equipped.includes(id)) return false;
  if (!modCompatible(cls, def)) return false;
  const open = slotsAt(level);
  const used = equipped.filter((e) => MOD_DEFS[e]?.slot === def.slot).length;
  if (used >= open[def.slot]) return false;
  // Une seule modification par groupe : une propriété identique ne s'empile pas.
  if (equipped.some((e) => MOD_DEFS[e]?.groupId === def.groupId)) return false;
  return true;
}

export function openSlot(level: number, equipped: string[]): ModSlot | null {
  const open = slotsAt(level);
  for (const s of ['weapon', 'chassis', 'passive'] as ModSlot[]) {
    const used = equipped.filter((e) => MOD_DEFS[e]?.slot === s).length;
    if (used < open[s]) return s;
  }
  return null;
}

/** Plafonds des multiplicateurs agrégés (après cumul des modifications). */
export const MOD_CAPS: Partial<Record<StatKey, [number, number]>> = {
  dmg: [0.4, 2.2],
  dealt: [0.4, 2.5],
  reload: [0.5, 3.5],
  taken: [0.4, 1.6],
  hp: [0.7, 1.4],
  move: [0.7, 1.35],
  resp: [0.5, 2],
  regen: [0.4, 2.5],
  knockTaken: [0.3, 1.5],
  pullTaken: [0.3, 1.5],
  slowTaken: [0.3, 1.5],
  ctrlDur: [0.4, 1.5],
};
